/**
 * Siding, measured on the walls rather than the roof.
 *
 * Every roof template reads `job_measurements`: squares, eave, rake, ridge.
 * Siding is not on the roof, and the numbers that size it are not on a roof
 * report — facade area, corners, window and door perimeter, starter, frieze,
 * fascia, soffit. Jonathon, 2026-09-30: "we will use hover report for its
 * measurements. there will be different fields for wall measurements than roof
 * measurements." So a siding job carries a wall measurement of its own
 * (`job_wall_measurements`), read off a Hover report or keyed in, and the
 * siding template's sections draw on it the way a roof template's draw on the
 * roof.
 *
 * The arithmetic here is Jonathon's, from the same conversation, and it is
 * pinned in siding.test.ts against the Hover report and the material order he
 * supplied for 2805 Saint Patrick Place (job 1076). Nothing here touches
 * money: rates live on products and the ladder does the arithmetic.
 */

/**
 * The roof type a siding template carries.
 *
 * Not a roof at all, but `roof_type` is the column that routes a template to
 * its path, its questions and its measurements, and a wall is one more thing
 * a template can be measured against. Free text on the document, never
 * renamed (CLAUDE.md rule 8).
 */
export const SIDING_ROOF_TYPE = "Siding";

export const isSidingType = (roofType: string | null | undefined): boolean =>
  (roofType ?? "").trim().toLowerCase() === SIDING_ROOF_TYPE.toLowerCase();

/* -------------------------------------------------------------------------
 * The walls
 * ---------------------------------------------------------------------- */

/** The numbers a wall measurement holds — a row of `job_wall_measurements`. */
export interface WallMeasurements {
  /** Siding facades, in square feet. Hover's "Facades". */
  siding_sqft?: unknown;
  /** Windows and doors, in square feet. Hover's "Openings". */
  openings_sqft?: unknown;
  /** Trim touching siding, in square feet. Hover's "Trims". */
  trim_sqft?: unknown;
  /**
   * Siding and trim together, zero waste, in square feet — the first row of
   * Hover's "Siding & Trim Only" table. What the panels are bought against.
   */
  siding_trim_sqft?: unknown;
  openings_qty?: unknown;
  /** Tops, sills and sides of every opening added up. */
  openings_perimeter_lf?: unknown;
  inside_corner_qty?: unknown;
  inside_corner_lf?: unknown;
  outside_corner_qty?: unknown;
  outside_corner_lf?: unknown;
  level_starter_lf?: unknown;
  sloped_trim_lf?: unknown;
  vertical_trim_lf?: unknown;
  level_frieze_lf?: unknown;
  sloped_frieze_lf?: unknown;
  eaves_fascia_lf?: unknown;
  rakes_fascia_lf?: unknown;
  soffit_lf?: unknown;
  soffit_sqft?: unknown;
  shutter_qty?: unknown;
  vent_qty?: unknown;
  stories?: unknown;
  /** The waste factor the rep picked. Hover offers 0, 10 and 18. */
  waste_pct?: unknown;
}

export interface WallMeasurementField {
  key: keyof WallMeasurements & string;
  label: string;
  /** Where on a Hover report the number is, for the person checking. */
  hint?: string;
}

/**
 * The wall measurement form, in order.
 *
 * Every key is a column on `job_wall_measurements`. Waste and stories are
 * asked separately, as pitch and waste are on the roof form.
 */
export const WALL_MEASUREMENT_FIELDS: readonly WallMeasurementField[] = [
  { key: "siding_sqft", label: "Siding sq ft", hint: "Areas › Facades" },
  { key: "openings_sqft", label: "Openings sq ft", hint: "Areas › Openings" },
  { key: "trim_sqft", label: "Trim sq ft", hint: "Areas › Trims" },
  {
    key: "siding_trim_sqft",
    label: "Siding & trim sq ft (zero waste)",
    hint: "Siding Waste Totals › Siding & Trim Only › Zero Waste",
  },
  {
    key: "openings_qty",
    label: "Windows & doors",
    hint: "Openings › Quantity",
  },
  {
    key: "openings_perimeter_lf",
    label: "Opening perimeter lf",
    hint: "Openings › Total Perimeter",
  },
  { key: "outside_corner_qty", label: "Outside corners" },
  { key: "outside_corner_lf", label: "Outside corner lf" },
  { key: "inside_corner_qty", label: "Inside corners" },
  { key: "inside_corner_lf", label: "Inside corner lf" },
  { key: "level_starter_lf", label: "Level starter lf" },
  { key: "sloped_trim_lf", label: "Sloped trim lf" },
  { key: "vertical_trim_lf", label: "Vertical trim lf" },
  { key: "level_frieze_lf", label: "Level frieze board lf" },
  { key: "sloped_frieze_lf", label: "Sloped frieze board lf" },
  { key: "eaves_fascia_lf", label: "Eaves fascia lf" },
  { key: "rakes_fascia_lf", label: "Rakes fascia lf" },
  { key: "soffit_lf", label: "Soffit lf", hint: "Soffit Summary › Totals" },
  {
    key: "soffit_sqft",
    label: "Soffit sq ft",
    hint: "Soffit Summary › Totals",
  },
  { key: "shutter_qty", label: "Shutters" },
  { key: "vent_qty", label: "Vents" },
];

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** A square is 100 square feet. */
const SQUARE_FEET = 100;

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Squares of siding to buy, before waste.
 *
 * Hover's "Siding & Trim Only" figure: the facades plus the trim that touches
 * them, openings left out. Jonathon, 2026-09-30, asked which Hover number
 * drives the panel count: "hover's siding and trim. i'll select waste factor."
 * Where a wall was keyed in without that total, the two parts are added.
 */
export function sidingSquaresOf(m: WallMeasurements): number {
  const total = num(m.siding_trim_sqft);
  const sqft = total > 0 ? total : num(m.siding_sqft) + num(m.trim_sqft);
  return round2(sqft / SQUARE_FEET);
}

/**
 * Squares of siding and trim with each wall's own waste factor, across every
 * wall on the job: what a siding part's headline line is measured in, the
 * same way the panels are bought.
 */
export function sidingSquaresWithWaste(
  walls: readonly WallMeasurements[],
): number {
  return walls.reduce((total, wall) => {
    const area = sidingSquaresOf(wall);
    if (area <= 0) return total;
    return total + area * (1 + num(wall.waste_pct) / 100);
  }, 0);
}

/**
 * Squares of siding coming off.
 *
 * What is on the wall now: the facades alone, and no waste — nobody tears
 * off more than is there. It sizes the dump trailers on a tear-off, one per
 * 13 squares (Jonathon, 2026-10-01: "1 dump trailer per 13 squares of
 * siding"); the crew's tear-off rate is paid on the siding like the layover.
 */
export function sidingRemovalSquaresOf(m: WallMeasurements): number {
  const facades = num(m.siding_sqft);
  return facades > 0 ? round2(facades / SQUARE_FEET) : sidingSquaresOf(m);
}

/**
 * Squares of wall the house wrap covers: siding, openings and trim together.
 *
 * Wrap goes over the whole wall before anything is cut out of it. Hover's
 * "Total" in the Siding column — 1,795 sq ft on job 1076, which is two rolls.
 */
export function houseWrapSquaresOf(m: WallMeasurements): number {
  const sqft = num(m.siding_sqft) + num(m.openings_sqft) + num(m.trim_sqft);
  return sqft > 0 ? round2(sqft / SQUARE_FEET) : sidingSquaresOf(m);
}

/**
 * Posts per corner, from how many storeys the house has.
 *
 * A corner post is ten feet, so a one-storey corner is one post and a
 * two-storey corner is two. Jonathon, 2026-09-30: "two posts per corner on a
 * two story house." Counted, not measured by length: Hover's corner length is
 * the de-duplicated run, and a post is cut to a corner, not spliced along a
 * wall.
 */
export function postsPerCorner(stories: unknown): number {
  return Math.max(1, Math.ceil(num(stories)));
}

export function outsideCornerPosts(m: WallMeasurements): number {
  return num(m.outside_corner_qty) * postsPerCorner(m.stories);
}

export function insideCornerPosts(m: WallMeasurements): number {
  return num(m.inside_corner_qty) * postsPerCorner(m.stories);
}

/**
 * Feet of J channel.
 *
 * Jonathon, 2026-09-30, on where it goes: around windows and doors, along the
 * edges of gables and rooflines, where siding meets the soffit under the
 * eaves, and at vertical wall transitions. On a Hover report that is the
 * openings' perimeter, the level and sloped frieze boards (the eave and gable
 * lines the siding runs up to), and the sloped and vertical trim.
 */
export function jChannelLf(m: WallMeasurements): number {
  return round2(
    num(m.openings_perimeter_lf) +
      num(m.level_frieze_lf) +
      num(m.sloped_frieze_lf) +
      num(m.sloped_trim_lf) +
      num(m.vertical_trim_lf),
  );
}

/** Feet of fascia: the eaves and the rakes together. */
export function fasciaLf(m: WallMeasurements): number {
  return round2(num(m.eaves_fascia_lf) + num(m.rakes_fascia_lf));
}

/** Feet of frieze board: level and sloped together. */
export function friezeLf(m: WallMeasurements): number {
  return round2(num(m.level_frieze_lf) + num(m.sloped_frieze_lf));
}

/** Squares of soffit, for the panels that cover it. */
export function soffitSquaresOf(m: WallMeasurements): number {
  return round2(num(m.soffit_sqft) / SQUARE_FEET);
}

/**
 * What a wall slot measures, worked out from the wall.
 *
 * One function so the generator and anything that explains a quantity agree.
 * Unknown keys fall through to the column of the same name, which is how the
 * plain lengths (starter, soffit) are read.
 */
export function wallMeasurement(key: string, m: WallMeasurements): number {
  switch (key) {
    case "siding_squares":
      return sidingSquaresOf(m);
    case "siding_removal_squares":
      return sidingRemovalSquaresOf(m);
    case "house_wrap_squares":
      return houseWrapSquaresOf(m);
    case "soffit_squares":
      return soffitSquaresOf(m);
    case "outside_corner_posts":
      return outsideCornerPosts(m);
    case "inside_corner_posts":
      return insideCornerPosts(m);
    case "j_channel_lf":
      return jChannelLf(m);
    case "siding_starter_lf":
      return num(m.level_starter_lf);
    case "openings_ea":
      return num(m.openings_qty);
    case "fascia_lf":
      return fasciaLf(m);
    case "frieze_lf":
      return friezeLf(m);
    default:
      return num((m as Record<string, unknown>)[key]);
  }
}

/** Every key `wallMeasurement` works out rather than reads. */
export const WALL_MEASUREMENT_KEYS: readonly string[] = [
  "siding_squares",
  "siding_removal_squares",
  "house_wrap_squares",
  "soffit_squares",
  "outside_corner_posts",
  "inside_corner_posts",
  "j_channel_lf",
  "siding_starter_lf",
  "openings_ea",
  "soffit_lf",
  "fascia_lf",
  "frieze_lf",
];
